import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import { FiMenu, FiMoreVertical, FiUser, FiLogOut } from 'react-icons/fi';
import { NAV_SECTIONS } from '../config/navConfig';
import { useAuth } from '../context/AuthContext';
import './AppRibbon.css';

const DESKTOP_MQ = '(min-width: 1025px)';
const RAIL_ICON = 76;   // icon-only width (px)
const RAIL_LIST = 236;  // expanded list width (px)

/**
 * AppRibbon — desktop-only fixed navigation rail.
 *
 * Reserves horizontal space via the `--cs-rail-width` custom property so page
 * content shrinks instead of sitting underneath. Hidden <=1024px via CSS,
 * where the existing hamburger sidebar remains in use.
 */
const AppRibbon = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logoutAction } = useAuth();
  const isAdmin = user?.role === 'admin';
  const isActive = (p) => location.pathname === p;

  // Stable key: user id can be briefly undefined across route remounts, which
  // would otherwise reset the saved view back to icon.
  const storageKey = 'cs-ribbon-view';
  const [view, setView] = useState(
    () => localStorage.getItem(storageKey) || 'icon'
  );

  // Hover tooltip is rendered fixed (outside the scroll area) to avoid clipping.
  const [tip, setTip] = useState(null);

  const toggleView = () => {
    const next = view === 'icon' ? 'list' : 'icon';
    setView(next);
    setTip(null);
    try { localStorage.setItem(storageKey, next); } catch (e) { /* ignore */ }
  };

  // Publish the rail width so the layout can reserve space (0 on mobile).
  useEffect(() => {
    const mq = window.matchMedia(DESKTOP_MQ);
    const apply = () => {
      const w = mq.matches ? (view === 'list' ? RAIL_LIST : RAIL_ICON) : 0;
      document.documentElement.style.setProperty('--cs-rail-width', `${w}px`);
    };
    apply();
    mq.addEventListener('change', apply);
    return () => mq.removeEventListener('change', apply);
  }, [view]);

  const sections = NAV_SECTIONS.filter((s) => !s.adminOnly || isAdmin);

  const showTip = (e, label) => {
    if (view !== 'icon') return;
    const r = e.currentTarget.getBoundingClientRect();
    setTip({ label, top: r.top + r.height / 2 });
  };

  const handleLogout = async () => {
    setTip(null);
    try { await logoutAction(); } catch (e) { /* ignore */ }
    navigate('/login');
  };

  return (
    <>
      {/* Shared gradient used to stroke every icon in the ribbon. */}
      <svg width="0" height="0" style={{ position: 'absolute' }} aria-hidden="true">
        <defs>
          <linearGradient id="csNavGrad" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="#667eea" />
            <stop offset="100%" stopColor="#764ba2" />
          </linearGradient>
        </defs>
      </svg>

      <nav
        className={`cs-ribbon cs-ribbon--${view}`}
        aria-label="Primary navigation"
        onMouseLeave={() => {
          setTip(null);
          if (view === 'list') {
            setView('icon');
            try { localStorage.setItem(storageKey, 'icon'); } catch (e) { /* ignore */ }
          }
        }}
      >
        <button
          type="button"
          className="cs-ribbon__toggle"
          onClick={toggleView}
          aria-label={view === 'icon' ? 'Switch to list view' : 'Switch to icon view'}
          title={view === 'icon' ? 'List view' : 'Icon view'}
        >
          {view === 'icon' ? <FiMenu /> : <FiMoreVertical />}
        </button>

        <div className="cs-ribbon__scroll">
          <div className="cs-ribbon__scroll-inner">
            {sections.map((section, si) => (
              <div className="cs-ribbon__group" key={section.category}>
                {si > 0 && <div className="cs-ribbon__divider" />}
                {view === 'list' && (
                  <div className="cs-ribbon__section-label">{section.category}</div>
                )}
                {section.items.map((item) => {
                  const Icon = item.icon;
                  return (
                    <Link
                      key={item.path}
                      to={item.path}
                      className={`cs-ribbon__bubble${isActive(item.path) ? ' active' : ''}`}
                      aria-current={isActive(item.path) ? 'page' : undefined}
                      aria-label={item.label}
                      onMouseEnter={(e) => showTip(e, item.label)}
                    >
                      <span className="cs-ribbon__icon-wrap">
                        {Icon ? <Icon className="cs-ribbon__icon" /> : null}
                      </span>
                      <span className="cs-ribbon__label">{item.label}</span>
                    </Link>
                  );
                })}
              </div>
            ))}
          </div>
        </div>

        {/* Account actions — pinned at the bottom for viewer & admin. */}
        <div className="cs-ribbon__footer">
          <div className="cs-ribbon__divider" />
          <Link
            to="/profile"
            className={`cs-ribbon__bubble${isActive('/profile') ? ' active' : ''}`}
            aria-current={isActive('/profile') ? 'page' : undefined}
            aria-label="Profile"
            onMouseEnter={(e) => showTip(e, 'Profile')}
          >
            <span className="cs-ribbon__icon-wrap"><FiUser className="cs-ribbon__icon" /></span>
            <span className="cs-ribbon__label">Profile</span>
          </Link>
          <button
            type="button"
            className="cs-ribbon__bubble cs-ribbon__bubble--logout"
            aria-label="Logout"
            onMouseEnter={(e) => showTip(e, 'Logout')}
            onClick={handleLogout}
          >
            <span className="cs-ribbon__icon-wrap"><FiLogOut className="cs-ribbon__icon" /></span>
            <span className="cs-ribbon__label">Logout</span>
          </button>
        </div>
      </nav>

      {/* Glass tooltip (icon view) — fixed so it never triggers a scrollbar. */}
      {view === 'icon' && tip && (
        <div className="cs-ribbon__tip" style={{ top: tip.top }} role="tooltip">
          {tip.label}
        </div>
      )}
    </>
  );
};

export default AppRibbon;
